import { toast } from 'react-toastify';

/**
 * Centralized toast notification utility.
 * Usage:
 *   import { showToast } from '../utils/toast';
 *   showToast.success('Item added!');
 *   showToast.error('Something went wrong');
 *   showToast.orderPlaced('ORD-12345');
 */

const defaultOptions = {
    position: 'top-right',
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
};

export const showToast = {
    success: (message, options = {}) =>
        toast.success(message, { ...defaultOptions, ...options }),

    error: (message, options = {}) =>
        toast.error(message, { ...defaultOptions, autoClose: 5000, ...options }),

    warning: (message, options = {}) =>
        toast.warning(message, { ...defaultOptions, ...options }),

    info: (message, options = {}) =>
        toast.info(message, { ...defaultOptions, ...options }),

    // Domain-specific toasts
    orderPlaced: (orderId) =>
        toast.success(`🎉 Order ${orderId} placed successfully!`, {
            ...defaultOptions,
            autoClose: 5000,
        }),

    orderStatusUpdate: (orderId, status) =>
        toast.info(`📦 Order ${orderId} is now ${status}`, defaultOptions),

    paymentSuccess: (amount) =>
        toast.success(`💰 Payment of ₹${amount} received!`, {
            ...defaultOptions,
            autoClose: 4000,
        }),

    paymentFailed: (message = 'Payment failed. Please try again.') =>
        toast.error(`❌ ${message}`, { ...defaultOptions, autoClose: 6000 }),

    cartUpdated: (action = 'updated') =>
        toast.success(`🛒 Cart ${action}!`, { ...defaultOptions, autoClose: 2000 }),

    itemOutOfStock: (itemName) =>
        toast.warning(`⚠️ ${itemName} is currently out of stock`, defaultOptions),

    loginSuccess: (name) =>
        toast.success(`👋 Welcome back${name ? `, ${name}` : ''}!`, defaultOptions),

    logoutSuccess: () =>
        toast.info('You have been logged out', { ...defaultOptions, autoClose: 2000 }),

    networkError: () =>
        toast.error('🌐 Network error. Please check your connection.', {
            ...defaultOptions,
            autoClose: 6000,
        }),

    // Promise-based toast for async operations
    promise: (promise, { pending, success, error } = {}) =>
        toast.promise(promise, {
            pending: pending || 'Processing...',
            success: success || 'Done!',
            error: error || 'Something went wrong',
        }),

    dismiss: (toastId) => toast.dismiss(toastId),
    dismissAll: () => toast.dismiss(),
};

export default showToast;
